import { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import { Link } from 'react-router-dom';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SOCIAL_PLATFORMS } from '../config/platformCapabilities';
import { apiPlatformToRouteId } from '../connection/utils/platformRouteId';
import type { SocialAccountRequest, SocialAccountResponse } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const PLATFORMS = SOCIAL_PLATFORMS.map((p) => ({
  value: p.apiPlatform,
  label: p.label,
}));

const empty: SocialAccountRequest = { platform: 'FACEBOOK', accountName: '', accountId: '' };

const OAUTH_RETURN = '/social/connect?tab=channels';

export function SocialChannelsPage({ embedded }: { embedded?: boolean } = {}) {
  const [data, setData] = useState<SocialAccountResponse[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<SocialAccountRequest>(empty);
  const [saving, setSaving] = useState(false);
  const [connectingId, setConnectingId] = useState<number | null>(null);

  const grouped = useMemo(() => {
    const map = new Map<string, SocialAccountResponse[]>();
    for (const row of data) {
      const list = map.get(row.platform) ?? [];
      list.push(row);
      map.set(row.platform, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const load = useCallback(async () => {
    try {
      const rows = await socialApi.listAccounts();
      setData(rows);
    } catch (err) {
      showApiError(err, 'Failed to load channels');
    }
  }, []);

  useEffect(() => {
    void load();
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') || params.get('demo')) {
      notify.success('Channel connected successfully');
      window.history.replaceState({}, '', OAUTH_RETURN);
      void load();
    }
  }, [load]);

  const save = async () => {
    if (!form.accountName.trim()) {
      notify.error('Account name is required');
      return;
    }
    setSaving(true);
    try {
      const account = await socialApi.connectAccount({
        ...form,
        accountId: form.accountId || `pending_${Date.now()}`,
      });
      notify.success('Channel added — finish connection below');
      setOpen(false);
      setForm(empty);
      await load();
      await connectChannel(account.id);
    } catch (err) {
      showApiError(err, 'Could not add channel');
    } finally {
      setSaving(false);
    }
  };

  const connectChannel = async (accountId: number) => {
    setConnectingId(accountId);
    try {
      const oauth = await socialApi.getOAuthUrl(accountId);
      if (oauth.url.includes('/oauth/demo/')) {
        await socialApi.demoConnect(accountId);
        notify.success('Connected (demo mode)');
        void load();
      } else {
        window.location.href = oauth.url;
      }
    } catch {
      try {
        await socialApi.demoConnect(accountId);
        notify.success('Connected (demo mode)');
        void load();
      } catch (inner) {
        showApiError(inner, 'Connection failed — see setup guide for this network');
      }
    } finally {
      setConnectingId(null);
    }
  };

  const disconnect = async (id: number) => {
    try {
      await socialApi.disconnectAccount(id);
      notify.success('Disconnected');
      void load();
    } catch (err) {
      showApiError(err, 'Disconnect failed');
    }
  };

  const reconnect = async (id: number) => {
    try {
      await socialApi.reconnectAccount(id);
      notify.success('Reconnect started');
      await connectChannel(id);
    } catch (err) {
      showApiError(err, 'Reconnect failed');
    }
  };

  return (
    <div>
      {!embedded && (
        <PageHeader
          title="Connected channels"
          description="Add multiple Pages, accounts, or locations per network under one login"
          breadcrumbs={[
            { label: 'Social', to: '/social' },
            { label: 'Connection Center', to: '/social/connect' },
            { label: 'Channels' },
          ]}
          actions={
            <Button onClick={() => setOpen(true)}>
              <AddIcon fontSize="small" /> Add another account
            </Button>
          }
        />
      )}
      {embedded && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            You can connect several accounts per platform (e.g. three Facebook Pages).
          </p>
          <Button onClick={() => setOpen(true)}>
            <AddIcon fontSize="small" /> Add account
          </Button>
        </div>
      )}

      {!embedded && (
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          Need help? Open the{' '}
          <Link to="/social/connect?tab=guides" className="text-link font-medium">
            setup guides
          </Link>{' '}
          or{' '}
          <Link to="/social/connect?tab=health" className="text-link font-medium">
            connection health
          </Link>
          .
        </p>
      )}

      {data.length === 0 && (
        <div className="card p-6 text-sm text-slate-600 dark:text-slate-400">
          No channels yet. Click <strong>Add another account</strong> and follow the prompts.
        </div>
      )}

      <div className="space-y-6">
        {grouped.map(([platform, accounts]) => {
          const routeId = apiPlatformToRouteId(platform);
          return (
            <section key={platform} className="card overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 dark:border-slate-700">
                <div>
                  <h3 className="font-semibold">{platform}</h3>
                  <p className="text-xs text-slate-500">
                    {accounts.length} account{accounts.length === 1 ? '' : 's'} connected
                  </p>
                </div>
                <Link
                  to={`/social/connect/guide/${routeId}`}
                  className="text-sm text-primary-600 hover:underline"
                >
                  Setup guide
                </Link>
              </div>
              <ul className="divide-y dark:divide-slate-700">
                {accounts.map((r) => (
                  <li
                    key={r.id}
                    className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">{r.accountName}</p>
                      <p className="truncate text-xs text-slate-500">
                        {r.connected ? 'Connected' : 'Not connected'}
                        {r.accountId ? ` · ${r.accountId}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!r.connected && (
                        <Button size="sm" onClick={() => void connectChannel(r.id)} loading={connectingId === r.id}>
                          Connect
                        </Button>
                      )}
                      {r.connected && (
                        <>
                          <Button size="sm" variant="secondary" onClick={() => void reconnect(r.id)}>
                            Reconnect
                          </Button>
                          <button
                            type="button"
                            className="text-sm text-slate-500 hover:text-slate-700"
                            onClick={() => void disconnect(r.id)}
                          >
                            Disconnect
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Add social account"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void save()} loading={saving}>
              <LinkIcon fontSize="small" /> Add & connect
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
          <Select
            label="Network"
            value={form.platform}
            onChange={(v) => setForm({ ...form, platform: v })}
            options={PLATFORMS}
          />
          <Input
            label="Display name"
            value={form.accountName}
            onChange={(e) => setForm({ ...form, accountName: e.target.value })}
          />
          <Input
            label="Account ID (optional)"
            value={form.accountId}
            onChange={(e) => setForm({ ...form, accountId: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
